import { Radio, Space } from "antd/es";
import { RadioGroupProps } from "antd/es/radio";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { PureComponent, ReactNode } from "react";
import isEqual from "react-fast-compare";
import { CommonProps } from "../../../components/web-components/common/common-props";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";

interface IKRadioGroupProps extends RadioGroupProps {
  space?: number;
  selectedFontColor?: string;
  selectedBgColor?: string;
  unselectedBgColor?: string;
}

interface IKRadioGroupState {}

class KRadioGroup extends PureComponent<IKRadioGroupProps & CommonProps, IKRadioGroupState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kradiogroup_${this.uniqueKey}`);

  constructor(props: IKRadioGroupProps) {
    super(props);
    this.state = {};
  }

  componentDidMount = () => {
    this.setDynamicStyle();
  };

  componentDidUpdate = (prevProps: IKRadioGroupProps) => {
    if (!isEqual(prevProps, this.props)) {
      this.setDynamicStyle();
    }
  };

  componentWillUnmount = () => {
    this.style.removeStyle();
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }

    let result = "";

    if (
      this.props.style?.color ||
      this.props.style?.fontSize ||
      this.props.style?.fontFamily ||
      this.props.style?.fontWeight ||
      this.props.unselectedBgColor
    ) {
      const color = `color: ${this.props.style?.color};`;
      const fontSize = `font-size: ${this.props.style?.fontSize};`;
      const fontFamily = `font-family: ${this.props.style?.fontFamily};`;
      const fontWeight = `font-weight: ${this.props.style?.fontWeight};`;
      const bgColor = `background-color: ${this.props.unselectedBgColor};`;

      result += `
      .${this.getClassName().trim()} .ant-radio-button-wrapper {
        ${this.props.style?.color && color}
        ${this.props.style?.fontSize && fontSize}
        ${this.props.unselectedBgColor && bgColor}
        ${this.props.style?.fontFamily && fontFamily}
        ${this.props.style?.fontWeight && fontWeight}
      }
      `;
    }

    if (this.props.style?.display === "inline") {
      result += `
          .${this.getClassName().trim()} {
            display: inline-block !important;
          }
          `;
    }

    if (this.props.selectedBgColor || this.props.selectedFontColor || this.props.style?.color) {
      const selectedBgColor = this.props.selectedBgColor
        ? `
        background-color: ${this.props.selectedBgColor} !important;`
        : "";

      const selectedFontColor = this.props.selectedFontColor
        ? `
        color: ${this.props.selectedFontColor} !important;`
        : "";

      result += `
        .${this.getClassName().trim()} .ant-radio-button-wrapper-checked {
          ${selectedBgColor}
          ${selectedFontColor}
        }
      `;
    }

    return result;
  };

  getClassName = () => {
    let result = "";
    if (this.props.className) {
      result = this.props.className;
    }
    if (!this.uniqueKey) {
      return result;
    }
    result = `${result} kradiogroup_${this.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  getProps = () => {
    const props: any = _.clone(this.props);

    if (props.options) {
      delete props.options;
    }
    if (props.className) {
      delete props.className;
    }

    if (props.children) delete props.children;

    if (props.style) {
      const style: any = _.clone(this.props.style);
      if (style.fontSize) delete style.fontSize;
      if (style.color) delete style.color;
      if (style.fontFamily) delete style.fontFamily;
      if (style.fontWeight) delete style.fontWeight;

      style.display = style.display === "block" ? "flex" : "inline-flex";

      props.style = style;
    }

    props.buttonStyle = "solid";
    props.className = this.getClassName();

    return props;
  };

  render(): ReactNode {
    return (
      <>
        <Radio.Group {...this.getProps()}>
          {this.props.space ? (
            <Space size={this.props.space} wrap>
              {this.props.children}
            </Space>
          ) : (
            <>{this.props.children}</>
          )}
        </Radio.Group>
      </>
    );
  }
}

const kRadioGroup = withCommonEvents(KRadioGroup);
export { kRadioGroup as KRadioGroup };
